import type { Currency, NetWorth } from "../../entities/ledger/types";
import { money } from "../../shared/lib/format";

interface AccountsSummaryProps {
  netWorth: NetWorth;
  baseCurrency: Currency;
  reportingCurrency: Currency;
  accountCount: number;
}

// The totals are the server's: every account counts once, on the side its
// position puts it. Adding up the cards here would count debts as assets.
export function AccountsSummary({ netWorth, baseCurrency, reportingCurrency, accountCount }: AccountsSummaryProps) {
  // A debt no Borrowing answers for is still counted — its amount is known —
  // but the missing Borrowing is a gap in the model, said out loud.
  const unattributed = (netWorth.liabilities ?? []).filter((item) => item.debtSource === "unattributed_account_debt" && item.amount);
  return (
    <div className="summary-grid">
      <article className="panel balance-card">
        <div className="card-label"><span>Assets, book value</span><span className="account-chip">{accountCount} {accountCount === 1 ? "account" : "accounts"}</span></div>
        <div className="balance-amount">{money(netWorth.assets.minor, netWorth.assets.currency)}</div>
        <p>{reportingCurrency !== baseCurrency && netWorth.reportingAssets ? `${money(netWorth.reportingAssets.minor, netWorth.reportingAssets.currency)} in the reporting currency. ` : ""}What your accounts hold, valued in {baseCurrency}.</p>
      </article>
      <article className="panel balance-card">
        <div className="card-label"><span>Debt, book value</span><span className="account-chip">{baseCurrency}</span></div>
        <div className="balance-amount">{money(netWorth.debt.minor, netWorth.debt.currency)}</div>
        <p>{reportingCurrency !== baseCurrency && netWorth.reportingDebt ? `${money(netWorth.reportingDebt.minor, netWorth.reportingDebt.currency)} in the reporting currency. ` : ""}Borrowing debts, and what accounts no Borrowing covers owe, valued in {baseCurrency}.</p>
        {unattributed.map((item) => (
          <p className="panel-note" key={item.accountId}>
            {money(item.amount!.minor, item.amount!.currency)} debt on {item.accountName} is not attributed to a Borrowing.
          </p>
        ))}
      </article>
      <article className="panel balance-card">
        <div className="card-label"><span>Net worth</span><span className="account-chip">{baseCurrency}</span></div>
        <div className="balance-amount">{money(netWorth.total.minor, netWorth.total.currency)}</div>
        <p>
          Assets minus debt, from each account's own history. Provider debt observations and Borrowing replay state
          remain separate and are shown in their own views.
          {netWorth.complete ? "" : " Some account could not be valued for lack of a rate; it is left out of these totals."}
        {netWorth.evidenceComplete === false ? " Some Borrowing debt still rests on evidence that is not settled — an unallocated repayment or a movement waiting to be recognised." : ""}
        </p>
      </article>
    </div>
  );
}
